import React, { memo, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import { logging } from '../../actions/log';
import { installSoft } from '../../actions/native';
import {
  DOWNLOAD_FINISHED,
  DOWNLOAD_ING,
  INSTALL_FINISHED,
  DOWNLOAD_FAILED,
  INSTALL_FAILED,
  DOWNLOAD_CANCELED,
} from '../../constants/softstatus';
import { PCStoreJsBridge } from '../../nativeapi';
import styles from './Button.module.less';
import CompetitiveProduct from '../CompetitiveProduct/CompetitiveProduct';

const selectLogInfo = createSelector(
  state => state.log,
  log => log.logInfo,
);

const selectDownloadSofts = createSelector(
  state => state.download,
  download => download.softs,
);

const selectInstalledSoft = createSelector(
  state => state.native,
  native => native.installedSoft,
);

const selectUpgradeSofts = createSelector(
  state => state.native,
  native => native.upgradeSofts,
);

const selectSilentInstSoft = createSelector(
  state => state.native,
  native => native.supportSilentInstSoft,
);

const selectIsEnabledSilent = createSelector(
  state => state.layout,
  layout => layout.isEnabledSilent,
);

export default memo(({ soft, info = {}, logInfo = {} }) => {
  const dispatch = useDispatch();
  const upgradeSofts = useSelector(selectUpgradeSofts); // 可更新
  const installedSofts = useSelector(selectInstalledSoft); // 已经安装
  const downloadSofts = useSelector(selectDownloadSofts); // 正在下载
  const silentInstSoft = useSelector(selectSilentInstSoft); // 支持快速静默安装的应用列表
  const isEnabledSilent = useSelector(selectIsEnabledSilent); // 是否打开快速静默安装

  const logInfoData = useSelector(selectLogInfo);

  const [fondSoft, setFondSoft] = useState(undefined);
  const [upgrade, setUpgrade] = useState(undefined);
  const [silentSoft, setSilentSoft] = useState(undefined);
  const [installed, setInstalled] = useState(undefined);
  const [linearGradient, setLinearGradient] = useState(
    'linear-gradient(to right, #ffffff00 0%, #ffffff00 0%)',
  );
  const [modalVisible, setModalVisible] = useState(false);

  const [buttonText, setButtonText] = useState(info.text || '安装');
  const [installSoftInfo, setInstalSoftInfo] = useState(soft);

  const handleInstallSoft = useCallback(
    (soft_info, button_Text, logInfo_para) => {
      if (!soft_info.softID) {
        return;
      }
      const logParam2 = {
        source: logInfo_para.source,
        source_p: logInfoData.source_p,
        source_pp: logInfoData.source_pp,
        softid: soft_info.softID,
        softName: soft_info.softName,
        softType: '0', // 安卓(1)还是PC(0)应用
        type: upgrade ? 'u' : 'd', // 更新还是下载
        url: soft_info.downLoadUrl,
      };
      // 当为搜索结果也跳转过来的，带上附加信息
      if (logInfo_para.isSearch) {
        logParam2.search_type = logInfoData.search_type;
        logParam2.search_word = logInfoData.search_word;
      }
      // 联想词softcard  做特殊处理 参数通过 logInfo (即logInfo_para) 传入
      if (
        logInfo_para.isSearch &&
        (logInfo_para.search_type === 4 || logInfo_para.search_type === 5)
      ) {
        logParam2.source_p = logInfo_para.source_p;
        logParam2.source_pp = logInfo_para.source_pp;
        logParam2.search_type = logInfo_para.search_type;
        logParam2.search_word = logInfo_para.search_word;
      }

      let installparams = null;
      if (isEnabledSilent && silentSoft && silentSoft.softID === soft_info.softID) {
        installparams = {};
        logParam2.type = upgrade ? 'xu' : 'xd';
        if (silentSoft.silentParamDic) {
          installparams.quiet = silentSoft.silentParamDic;
        }
        if (silentSoft.silentDirParamDic) {
          installparams.todir = silentSoft.silentDirParamDic;
        }
      }

      if (
        button_Text === info.text ||
        button_Text === '安装' ||
        button_Text === '升级' ||
        button_Text === '快装'
      ) {
        // 按钮点击打桩
        dispatch(
          logging({
            category: 'C',
            action: 'Install',
            label: '',
            param2: logParam2,
            param3: soft_info.bizInfo || null,
          }),
        );
        const softInfo = { ...soft_info, logInfo: logParam2, installparams, isClick: 'click' };
        if (info.isCompetitive && button_Text !== '升级') {
          setInstalSoftInfo(softInfo);
          setModalVisible(true);
          return;
        }
        dispatch(installSoft(softInfo));
      } else if (button_Text === '打开') {
        // 按钮点击打桩
        logParam2.type = 'o';
        dispatch(
          logging({
            category: 'C',
            action: 'Install',
            label: '',
            param2: logParam2,
            param3: soft_info.bizInfo || null,
          }),
        );
        PCStoreJsBridge.openSoft(installed);
      }
    },
    [dispatch, installed, silentSoft, isEnabledSilent],
  );

  // 查询当前是否已经安装
  useEffect(() => {
    if (upgradeSofts) {
      setUpgrade(
        upgradeSofts.find(soft_ => soft_.softID === soft.softID || soft_.SoftID === soft.softID),
      );
    }
  }, [soft, upgradeSofts]);

  // 查询当前是否已经安装 并且是否可升级
  useEffect(() => {
    if (installedSofts) {
      setInstalled(
        installedSofts.find(soft_ => soft_.softID === soft.softID || soft_.SoftID === soft.softID),
      );
    }
  }, [soft, installedSofts]);

  // 查询是否打开静默安装的开关
  // 查询当前是否已经安装 并且是否可升级
  useEffect(() => {
    if (silentInstSoft && isEnabledSilent) {
      setSilentSoft(silentInstSoft.find(soft_ => soft_.softID === soft.softID));
    }
  }, [soft, silentInstSoft, isEnabledSilent]);

  // 更新按钮显示状态
  useEffect(() => {
    // 按钮info传入百分比进度颜色
    const progressColor = info.progressColor || '#CAF3E1';
    // 按钮info传入百分比进度将要覆盖的背景颜色
    const backgroundColor = info.backgroundColor || '#ffffff00';
    let linear_Gradient = `linear-gradient(to right, ${progressColor} 0%, ${backgroundColor} 0%)`;
    let button_Text = info.text || '安装';
    const found = downloadSofts && downloadSofts.find(soft_ => soft_.softID === soft.softID);

    // 通知父元素按钮状态
    if (found && found.softID) {
      if (found.status === DOWNLOAD_ING) {
        button_Text = `${found.progress || 0}%`;
        linear_Gradient = `linear-gradient(to right, ${progressColor} ${found.progress ||
          0}%, ${backgroundColor} ${found.progress || 0}%)`;
      } else if (found.status === DOWNLOAD_FINISHED) {
        button_Text = '安装中';
        linear_Gradient = `linear-gradient(to right, ${progressColor} 100%, ${backgroundColor} 100%)`;
      } else if (found.status === INSTALL_FINISHED) {
        button_Text = '打开';
      } else if (
        found.status === DOWNLOAD_FAILED ||
        found.status === INSTALL_FAILED ||
        found.status === DOWNLOAD_CANCELED
      ) {
        // 下载安装失败时  按钮状态变为之前状态
        button_Text = upgrade ? '升级' : info.text || '安装';
      }
    } else if (upgrade) {
      button_Text = '升级';
    } else if (installed) {
      button_Text = '打开';
    } else if (silentSoft && isEnabledSilent) {
      button_Text = '快装';
    }
    if (buttonText !== button_Text) setButtonText(button_Text);
    if (linearGradient !== linear_Gradient) setLinearGradient(linear_Gradient);
    setFondSoft(found);
  }, [dispatch, soft, downloadSofts, upgrade, installed, silentSoft, isEnabledSilent]);

  const handleCancelModal = useCallback(() => {
    setModalVisible(false);
  });

  return (
    <>
      <div
        style={
          fondSoft && fondSoft.status && linearGradient ? { backgroundImage: linearGradient } : {}
        }
        className={
          fondSoft && fondSoft.status
            ? `${styles.soft_btn} ${styles.soft_button_busing} ${info.className_busing || ''}`
            : `${styles.soft_btn} 
            ${styles.soft_button} 
            ${info.className || ''}
            ${buttonText === '打开' && ((info && info.className_open) || styles.soft_button_open)}`
        }
        onClick={e => {
          e.preventDefault();
          // e.stopPropagation();
          handleInstallSoft(soft, buttonText, logInfo);
        }}
      >
        <span className={styles.soft_btn_text}>{buttonText}</span>
      </div>
      {modalVisible && (
        <CompetitiveProduct
          visible={modalVisible}
          softInfo={{
            ...installSoftInfo,
            SoftID: installSoftInfo.softID || installSoftInfo.SoftID,
          }}
          handleCancel={handleCancelModal}
          logInfo={logInfo}
          button_Text={buttonText}
        />
      )}
    </>
  );
});
